import type {
  NormalizedRun,
  ProjectedAgent,
  ProjectedChat,
} from '../../api/types'
import type { AABB } from '../colliders'
import { rotateStationOffset, STAND_LOCAL } from './layout'

export const CAFE_QUIET_ROOM = {
  centerX: 8.7,
  centerZ: -5.2,
  width: 7,
  depth: 7,
  height: 2.65,
  frontZ: -1.7,
} as const

/** Clear threshold and standing approach for the quiet room's 1.5 m door. */
export const CAFE_QUIET_ROOM_DOOR_APPROACH: AABB = {
  minX: CAFE_QUIET_ROOM.centerX - 0.75,
  maxX: CAFE_QUIET_ROOM.centerX + 0.75,
  minY: 0,
  maxY: CAFE_QUIET_ROOM.height,
  minZ: CAFE_QUIET_ROOM.frontZ - 0.15,
  maxZ: CAFE_QUIET_ROOM.frontZ + 1.85,
}

/** Unobstructed arrival promenade from the south door to the shared table. */
export const CAFE_CENTRAL_PROMENADE: AABB = {
  minX: -0.8,
  maxX: 0.8,
  minY: 0,
  maxY: 2.5,
  minZ: 0.4,
  maxZ: 10,
}

/** Guest side of the service bar; no furniture may consume this work aisle. */
export const CAFE_SERVICE_AISLE: AABB = {
  minX: -12.2,
  maxX: -7,
  minY: 0,
  maxY: 2.5,
  minZ: -7.55,
  maxZ: -6.15,
}

/** Standing avatars reserve a real shoulder/animation envelope around props. */
export const CAFE_AGENT_BODY_RADIUS = 0.5
export const CAFE_AGENT_FURNITURE_GAP = 0.15

/** Bar canopy stops short of the north projection wall and its cable chase. */
export const CAFE_COUNTER_CANOPY = {
  x: -9.8,
  y: 2.72,
  z: -8.1,
  width: 5.8,
  height: 0.12,
  depth: 2,
  northClearZ: -9.35,
} as const

export const CAFE_CONVERSATION_TABLES = [
  { id: 'west', x: -8.2, z: 2.7 },
  { id: 'north-west', x: -7, z: -2.9 },
] as const

export const CAFE_COMMON_TABLE = {
  x: 0,
  z: -4,
  actionX: 0,
  actionZ: -1.75,
} as const

export const CAFE_QUIET_SEATING = {
  couchX: 8.7,
  couchZ: -7.75,
  tableX: 8.7,
  tableZ: -6.1,
} as const

export const MAX_CAFE_AGENTS = 6

export interface CafeSeatSlot {
  id: string
  room: string
  /** Exact local-space position of the visible standing character. */
  x: number
  z: number
  /** Invisible station anchor used by the shared animated-character contract. */
  stationX: number
  stationZ: number
  stationRotY: number
}

function seatSlot(
  id: string,
  room: string,
  x: number,
  z: number,
  focusX: number,
  focusZ: number,
): CafeSeatSlot {
  const facingYaw = (
    Math.atan2(focusX - x, focusZ - z) * 180 / Math.PI + 360
  ) % 360
  const stationRotY = (facingYaw - 180 + 360) % 360
  const [offsetX, offsetZ] = rotateStationOffset(
    STAND_LOCAL.dx,
    STAND_LOCAL.dz,
    stationRotY,
  )
  return {
    id,
    room,
    x,
    z,
    stationX: x - offsetX,
    stationZ: z - offsetZ,
    stationRotY,
  }
}

/**
 * Six bounded, deterministic social positions. They sit beside — never on —
 * the measured furniture and face the conversation surface they belong to.
 */
export const CAFE_AGENT_SLOTS: readonly CafeSeatSlot[] = [
  seatSlot(
    'conversation-west-south',
    'CAFÉ · West conversation',
    -8.2,
    5.15,
    CAFE_CONVERSATION_TABLES[0].x,
    CAFE_CONVERSATION_TABLES[0].z,
  ),
  seatSlot(
    'conversation-west-side',
    'CAFÉ · West conversation',
    -10.65,
    2.7,
    CAFE_CONVERSATION_TABLES[0].x,
    CAFE_CONVERSATION_TABLES[0].z,
  ),
  seatSlot(
    'conversation-north-south',
    'CAFÉ · Focus conversation',
    -7,
    -0.45,
    CAFE_CONVERSATION_TABLES[1].x,
    CAFE_CONVERSATION_TABLES[1].z,
  ),
  seatSlot(
    'common-west',
    'CAFÉ · Common project table',
    -3.45,
    -4,
    CAFE_COMMON_TABLE.x,
    CAFE_COMMON_TABLE.z,
  ),
  seatSlot(
    'common-east',
    'CAFÉ · Common project table',
    3.45,
    -4,
    CAFE_COMMON_TABLE.x,
    CAFE_COMMON_TABLE.z,
  ),
  seatSlot(
    'quiet-room',
    'CAFÉ · Quiet room',
    10.8,
    -3.55,
    CAFE_QUIET_SEATING.tableX,
    CAFE_QUIET_SEATING.tableZ,
  ),
] as const

export interface CafeProjectedAgent {
  agent: ProjectedAgent
  slot: CafeSeatSlot
  boundRunId?: string
}

export interface CafeProjectionState {
  state:
    | 'fresh-populated'
    | 'fresh-empty'
    | 'loading'
    | 'stale-last-good'
    | 'disconnected'
  agents: readonly CafeProjectedAgent[]
  totalAgents: number | null
  agentOverflow: number | null
  boundConversationCount: number | null
  writableConversationCount: number | null
}

const compareText = (first: string, second: string): number =>
  first < second ? -1 : first > second ? 1 : 0

function bindRuns(
  agents: readonly ProjectedAgent[],
  runs: readonly NormalizedRun[],
): ReadonlyMap<string, string> {
  const projectedIds = new Set(agents.map((agent) => agent.id))
  const candidates = runs
    .filter((run) =>
      run.status === 'running' &&
      !run.stale &&
      typeof run.agentId === 'string' &&
      projectedIds.has(run.agentId))
    .sort((first, second) =>
      compareText(second.updatedAt, first.updatedAt) ||
      compareText(first.id, second.id))
  const result = new Map<string, string>()
  for (const run of candidates) {
    if (!run.agentId || result.has(run.agentId)) continue
    result.set(run.agentId, run.id)
  }
  return result
}

function staleProjection(lastGood?: CafeProjectionState): CafeProjectionState {
  return {
    state: 'stale-last-good',
    agents: lastGood?.agents.map((entry) => ({
      agent: { ...entry.agent },
      slot: entry.slot,
      ...(entry.boundRunId ? { boundRunId: entry.boundRunId } : {}),
    })) ?? [],
    totalAgents: lastGood?.totalAgents ?? null,
    agentOverflow: lastGood?.agentOverflow ?? null,
    boundConversationCount: lastGood?.boundConversationCount ?? null,
    writableConversationCount: lastGood?.writableConversationCount ?? null,
  }
}

/**
 * Pure projection of Portal-owned identity/chat truth into bounded Café slots.
 * Generic and orphan chats never contribute to an agent-bound conversation.
 */
export function layoutCafeProjection(
  status: 'disconnected' | 'loading' | 'fresh' | 'stale',
  agents: readonly ProjectedAgent[],
  chats: readonly ProjectedChat[],
  runs: readonly NormalizedRun[] = [],
  lastGood?: CafeProjectionState,
): CafeProjectionState {
  if (status === 'stale') return staleProjection(lastGood)
  if (status !== 'fresh') {
    return {
      state: status,
      agents: [],
      totalAgents: null,
      agentOverflow: null,
      boundConversationCount: null,
      writableConversationCount: null,
    }
  }

  const orderedAgents = [...agents].sort((first, second) =>
    compareText(first.id, second.id))
  const validAgentIds = new Set(orderedAgents.map((agent) => agent.id))
  const boundChats = chats.filter((chat) =>
    typeof chat.agentId === 'string' && validAgentIds.has(chat.agentId))
  const runByAgentId = bindRuns(orderedAgents, runs)
  const placedAgents = orderedAgents
    .slice(0, MAX_CAFE_AGENTS)
    .map((agent, index): CafeProjectedAgent => {
      const boundRunId = runByAgentId.get(agent.id)
      return {
        agent: { ...agent },
        slot: CAFE_AGENT_SLOTS[index],
        ...(boundRunId ? { boundRunId } : {}),
      }
    })

  return {
    state: orderedAgents.length > 0 || boundChats.length > 0
      ? 'fresh-populated'
      : 'fresh-empty',
    agents: placedAgents,
    totalAgents: orderedAgents.length,
    agentOverflow: Math.max(0, orderedAgents.length - MAX_CAFE_AGENTS),
    boundConversationCount: boundChats.length,
    writableConversationCount: boundChats.filter((chat) => chat.canSend).length,
  }
}

function countOrUnavailable(value: number | null, label: string): string {
  return value === null ? `${label} · NOT PROJECTED` : `${value} ${label}`
}

/** Physical panel copy derived only from one coherent projection snapshot. */
export function cafeProjectionLines(
  projection: CafeProjectionState,
): readonly [string, string, string] {
  if (projection.state === 'loading') {
    return [
      'TEAM PRESENCE · PROJECTING',
      'BOUND CHATS · PROJECTING',
      'MESSAGE ACCESS · PROJECTING',
    ]
  }
  if (projection.state === 'disconnected') {
    return [
      'TEAM PRESENCE · NOT PROJECTED',
      'BOUND CHATS · NOT PROJECTED',
      'MESSAGE ACCESS · NOT PROJECTED',
    ]
  }
  const prefix = projection.state === 'stale-last-good' ? 'STALE · ' : ''
  const placed = projection.agents.length
  const team = projection.totalAgents === null
    ? 'TEAM PRESENCE · NOT PROJECTED'
    : `${projection.totalAgents} TEAM MEMBERS · ${placed} IN CAFÉ`
  return [
    `${prefix}${team}`,
    `${prefix}${countOrUnavailable(
      projection.boundConversationCount,
      'BOUND CONVERSATIONS',
    )}`,
    `${prefix}${countOrUnavailable(
      projection.writableConversationCount,
      'READY TO MESSAGE',
    )}`,
  ]
}

export function cafeProjectionKey(projection: CafeProjectionState): string {
  return JSON.stringify({
    state: projection.state,
    agents: projection.agents.map((entry) => [
      entry.agent.id,
      entry.agent.name,
      entry.agent.canStartChat ?? null,
      entry.slot.id,
      entry.boundRunId ?? null,
    ]),
    totalAgents: projection.totalAgents,
    agentOverflow: projection.agentOverflow,
    boundConversationCount: projection.boundConversationCount,
    writableConversationCount: projection.writableConversationCount,
  })
}
